import { useTheme } from 'modules/styles'
import {
  STYLES_ALIGN_ITEMS,
  STYLES_FLEX_DIRECTION,
  STYLES_JUSTIFY_CONTENT
} from 'modules/styles/constants/index.cjs'
import * as React from 'react'
import getStyleComponentUtil from 'modules/component/utils/get-style'

export function View (propParams) {
  const theme = useTheme()
  const {
    style,
    props
  } = getStyleAndProps(
    propParams,
    theme
  )
  return (
    <div
      {...props}
      style={style}
    />
  )
}

const SPACE_SHORTHAND = {
  p: 'padding',
  h: 'height',
  height: 'height',
  w: 'width',
  mW: 'maxWidth',
  width: 'width',
  px: 'paddingHorizontal',
  py: 'paddingVertical',
  pl: 'paddingLeft',
  pr: 'paddingRight',
  pb: 'paddingBottom',
  pt: 'paddingTop',
  m: 'margin',
  mx: 'marginHorizontal',
  my: 'marginVertical',
  ml: 'marginLeft',
  mr: 'marginRight',
  mb: 'marginBottom',
  mt: 'marginTop'
}

const VERTICAL_HORIZONTAL_KEYS = {
  [SPACE_SHORTHAND.px]: [
    'paddingLeft',
    'paddingRight'
  ],
  [SPACE_SHORTHAND.py]: [
    'paddingTop',
    'paddingBottom'
  ],
  [SPACE_SHORTHAND.mx]: [
    'marginLeft',
    'marginRight'
  ],
  [SPACE_SHORTHAND.my]: [
    'marginTop',
    'marginBottom'
  ]
}

const PERCENT_OR_VALUE_SHORTHAND = {
  width: 'width',
  height: 'height'
}

const ONE_OR_VALUE_SHORTHAND = {
  f: 'flex'
}

const OTHER_SHORTHAND = {
  d: 'display',
  z: 'zIndex',
  j: 'justifyContent',
  a: 'alignItems',
  bdr: 'borderRadius',
  bd: 'border',
  pos: 'position',
  bgc: 'backgroundColor',
  ov: 'overflow',
  wrap: 'flexWrap',
  ta: 'textAlign',
  bdrBottomLeft: 'borderBottomLeftRadius',
  bdrBottomRight: 'borderBottomRightRadius',
  bdrTopLeft: 'borderTopLeftRadius',
  bdrTopRight: 'borderTopRightRadius'
}

const STYLE_PROP_KEY_SHORTHAND_TO_LONG_KEY = {
  ...SPACE_SHORTHAND, ...ONE_OR_VALUE_SHORTHAND
}

const BORDER_RADIUS_ARR = [
  'bdr',
  'bdrBottomLeft',
  'bdrBottomRight',
  'bdrTopLeft',
  'bdrTopRight'
]

const ATTR_ID_TYPE = {
  SPACE: 1,
  ONE_OR_VALUE: 2
}

const ATTR_ID_TYPE_TO_FORMATTER = {
  [ATTR_ID_TYPE.SPACE]: ({
    theme,
    longKey,
    value,
    isTrueBoolean,
    isNumber
  }) => {
    const space = theme.spaces.default
    const isPercentageShorthand = PERCENT_OR_VALUE_SHORTHAND[longKey]
    if (isPercentageShorthand && isTrueBoolean) {
      value = '100%'
    } else if (isTrueBoolean) {
      value = `${space}px`
    } else if (isNumber) {
      value = `${space * value}px`
    }
    return value
  },
  [ATTR_ID_TYPE.ONE_OR_VALUE]: ({
    value,
    isTrueBoolean
  }) => {
    if (isTrueBoolean) {
      value = 1
    }
    return value
  }
}

function getLongKeyToAttrId (
  itemsObj,
  attrIdType
) {
  return Object.values(itemsObj)
    .reduce(
      (
        obj,
        longKey
      ) => {
        obj[longKey] = attrIdType
        return obj
      },
      {}
    )
}

const LONG_KEY_TO_ATTR_ID_TYPE = {
  ...getLongKeyToAttrId(
    SPACE_SHORTHAND,
    ATTR_ID_TYPE.SPACE
  ),
  ...getLongKeyToAttrId(
    ONE_OR_VALUE_SHORTHAND,
    ATTR_ID_TYPE.ONE_OR_VALUE
  )
}

function getFormattedValue (
  longKey,
  value,
  theme
) {
  const attrIdType = LONG_KEY_TO_ATTR_ID_TYPE[longKey]
  if (!attrIdType) {
    console.log(
      '[View#getStyleAndProps] unrecognized longKey: ',
      longKey
    )
    return value
  }

  const isTrueBoolean = value === true
  const isNumber = typeof value === 'number'
  const isEM = typeof value === 'string' && value.endsWith('em')
  const formatter = ATTR_ID_TYPE_TO_FORMATTER[attrIdType]
  value = formatter({
    longKey,
    value,
    isTrueBoolean,
    isNumber,
    isEM,
    theme
  })
  return value
}

function getStyleAndProps (
  propParams = {},
  theme
) {
  const {
    style = {},
    ...props
  } = propParams

  const display = props.d
  style[OTHER_SHORTHAND.d] = display || 'flex'
  if (props.d) {
    delete props.d
  }

  // console.log('display: ', display)
  // console.log('style[OTHER_SHORTHAND.d]: ', style[OTHER_SHORTHAND.d])
  // console.log('style.flexDirection: ', style.flexDirection)
  if (style[OTHER_SHORTHAND.d] === 'flex' && !style.flexDirection) {
    style.flexDirection = 'column'
  }

  const newProps = Object
    .keys(props)
    .reduce(
      (
        obj,
        shorthand
      ) => {
        const value = props[shorthand]
        const longKey = STYLE_PROP_KEY_SHORTHAND_TO_LONG_KEY[shorthand]
        if (!longKey) {
          obj[shorthand] = value
          return obj
        }
        const longKeys = VERTICAL_HORIZONTAL_KEYS[longKey] || [longKey]
        longKeys.forEach(lK => {
          style[lK] =
            getFormattedValue(
              lK,
              value,
              theme
            )
        })
        return obj
      },
      {}
    )

  if (newProps.centered != null) {
    style.alignItems = 'center'
    style.justifyContent = 'center'
    delete newProps.centered
  }

  const backgroundColor = newProps.bgc
  if (typeof backgroundColor !== 'undefined') {
    style[OTHER_SHORTHAND.bgc] =
      typeof backgroundColor === 'boolean'
        ? theme.colors.background
        : theme.colors[backgroundColor] || backgroundColor
    delete newProps.bgc
  }

  const borderRadius = newProps.bdr
  if (typeof borderRadius !== 'undefined') {
    style[OTHER_SHORTHAND.bdr] =
      typeof borderRadius === 'boolean'
        ? theme.borderRadius.medium
        : theme.borderRadius[borderRadius] || borderRadius
    delete newProps.bdr
  }

  const zIndex = newProps.z
  if (typeof zIndex !== 'undefined') {
    style[OTHER_SHORTHAND.z] =
      typeof zIndex === 'boolean'
        ? theme.zIndex.background
        : theme.zIndex[zIndex] || zIndex
    delete newProps.z
  }

  const textAlign = newProps.ta
  if (textAlign) {
    style.textAlign = textAlign === true
      ? 'center'
      : textAlign
    delete newProps.ta
  }

  const je = newProps.je
  if (je) {
    style.justifyContent = je === true
      ? 'flex-end'
      : je
    delete newProps.je
  }

  BORDER_RADIUS_ARR.forEach(key => {
    const borderRadius = newProps[key]
    if (borderRadius === false) {
      return
    }
    if (typeof borderRadius !== 'undefined') {
      style[OTHER_SHORTHAND[key]] =
        borderRadius === true
          ? theme.borderRadius.medium
          : (
              theme.borderRadius?.[borderRadius] || borderRadius
            )
      delete newProps[key]
    }
  })

  const flexWrap = newProps.wrap
  if (typeof flexWrap !== 'undefined') {
    style[OTHER_SHORTHAND.wrap] =
      typeof flexWrap === 'boolean'
        ? 'wrap'
        : flexWrap
    delete newProps.wrap
  }

  const position = newProps.pos
  if (typeof position !== 'undefined') {
    style[OTHER_SHORTHAND.pos] =
      typeof position === 'boolean'
        ? 'pos'
        : position
    delete newProps.pos
  }

  const border = newProps.bd
  if (border) {
    style.borderWidth =
      typeof border === 'boolean'
        ? 1
        : null
    style.borderColor = theme.colors.border
    style.borderStyle = 'solid'
    delete newProps.bd
  }

  const justifyContent = newProps.jc
  if (justifyContent != null) {
    style.justifyContent =
      typeof justifyContent === 'boolean'
        ? 'center'
        : justifyContent
    delete newProps.jc
  }

  const flexDirectionRow = newProps.row
  if (flexDirectionRow != null) {
    style.flexDirection =
      flexDirectionRow === true
        ? 'row'
        : 'column'
    delete newProps.row
  }

  const alignItems = newProps.ac
  if (typeof alignItems !== 'undefined') {
    style.alignItems = alignItems === true
      ? 'center'
      : alignItems
    delete newProps.ac
  }

  const alignItemsStart = newProps.as
  if (alignItemsStart === true) {
    style.alignItems = 'flex-start'
    delete newProps.as
  }

  const alignItemsEnd = newProps.ae
  if (alignItemsEnd === true) {
    style.alignItems = 'flex-end'
    delete newProps.ae
  }

  return {
    style: getStyleComponentUtil(
      style,
      newProps.style
    ),
    props: newProps
  }
}

View.ALIGN = STYLES_ALIGN_ITEMS
View.DIRECTION = STYLES_FLEX_DIRECTION
View.JUSTIFY = STYLES_JUSTIFY_CONTENT

View.getStyleAndProps = getStyleAndProps
